// client/src/components/coi/bd/lunchPosts.ts
import { MEAL_LABEL } from "@shared/meals";

export interface LunchPerson { id: string; name: string; firmId: string | null }
export interface LunchForm { meal: boolean; occurredAt: string; note: string }
export interface LunchPost {
  type: "meeting"; meal?: true; title: string; content: string; occurredAt: string; companyId?: string; contactIds: string[];
}

/**
 * One activity per firm, the rule calendar sync follows (one row per event per
 * company): POST /api/activities requires every listed person to belong to the
 * row's company. A person with no firm gets a row of their own.
 */
export function lunchPosts(people: LunchPerson[], form: LunchForm): LunchPost[] {
  const groups = new Map<string, LunchPerson[]>();
  for (const p of people) {
    const k = p.firmId ?? `solo:${p.id}`;
    groups.set(k, [...(groups.get(k) ?? []), p]);
  }
  const title = `${form.meal ? MEAL_LABEL : "Meeting"} with ${people.map((p) => p.name).join(", ")}`;
  return [...groups.values()].map((g) => ({
    type: "meeting" as const,
    ...(form.meal ? { meal: true as const } : {}),
    title,
    content: form.note.trim(),
    occurredAt: form.occurredAt,
    ...(g[0].firmId ? { companyId: g[0].firmId } : {}),
    contactIds: g.map((p) => p.id),
  }));
}

/** Identity of a post across retries: its firm, or the lone person with no firm. */
export const postKey = (p: LunchPost) => p.companyId ?? `solo:${p.contactIds[0]}`;

/** The posts a retry still has to send, so a partial save never duplicates a firm. */
export function remainingPosts(posts: LunchPost[], saved: ReadonlySet<string>): LunchPost[] {
  return posts.filter((p) => !saved.has(postKey(p)));
}

/** Today on this device as YYYY-MM-DD, which is what a date input shows. todayIso() is UTC and runs a day ahead on Central evenings. */
export function localYmd(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}
